import React, { useContext, createContext, useState, useEffect } from "react";
import type { Session } from "@supabase/supabase-js";

import { authService, supabase } from "../lib/supabase";
import { type useRouter } from "@tanstack/react-router";
import type { UserDTO } from "@palman/api";

interface AuthProviderProps {
  router: ReturnType<typeof useRouter>;
  children: React.ReactNode;
}

export type AuthContextType = {
  session: Session | null;
  user: UserDTO | null;
  isLoading: boolean;
  setUser: (user: UserDTO) => void;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  isLoading: true,
  setUser: () => {},
  refreshUser: async () => {},
});

const AuthenticationProvider = ({ router, children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserDTO | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const refreshUser = async () => {
    try {
      const { user: updatedUser, error } = await authService.getUser();
      if (error) {
        console.error("Error refreshing user:", error);
        return;
      }
      setUser(updatedUser);
    } catch (error) {
      console.error("Error refreshing user:", error);
    }
  };

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setData();

        if (!session) {
          router.invalidate();
        }
      },
    );

    const setData = async () => {
      const { session, error: sessionError } = await authService.getSession();
      if (sessionError) throw sessionError;

      const { user, error: userError } = await authService.getUser();
      if (userError) console.error(userError);

      setSession(session);
      setUser(user);

      setIsLoading(false);
    };

    setData();

    return () => {
      listener?.subscription.unsubscribe();
    };
  }, [router]);

  const value = {
    session,
    user,
    isLoading,
    refreshUser,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuthentication = () => {
  return useContext(AuthContext);
};

export default AuthenticationProvider;
export { useAuthentication };
